import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2, Search, Star, TrendingDown, TrendingUp } from 'lucide-react';
import { Input } from '../ui/input';
import { RecommendationBadge } from './RecommendationBadge';
import { VerdictChip } from './Verdict';
import MobileCoinCard from './MobileCoinCard';
import LivePrice from './LivePrice';
import ScannerLegend from './ScannerLegend';
import { stageStyle, stageText } from './RadarCard';

const fmtPrice = (v) => {
    if (v === null || v === undefined) return '—';
    if (v >= 1000) return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
    if (v >= 1) return v.toLocaleString('en-US', { maximumFractionDigits: 4 });
    return v.toLocaleString('en-US', { maximumFractionDigits: 8 });
};
const fmtVol = (v) => {
    if (!v) return '—';
    if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
    if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
    if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
    return `${v.toFixed(0)}`;
};

const ScoreBar = ({ value, color = 'emerald' }) => {
    const pct = Math.max(0, Math.min(100, value));
    const colorMap = {
        emerald: 'bg-emerald-400',
        rose: 'bg-rose-500',
        amber: 'bg-amber-400',
        zinc: 'bg-slate-400',
    };
    return (
        <div className="flex items-center gap-2 min-w-[80px]">
            <div className="h-1 w-14 bg-slate-600 overflow-hidden rounded-sm">
                <div className={`h-full ${colorMap[color]}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="text-[11px] font-mono text-slate-300 tabular-nums w-7 text-right">{pct.toFixed(0)}</span>
        </div>
    );
};

const TAG_TEXT = {
    pump: { title: 'YUKARI PATLAMA', action: 'Erken girme, hacimli kırılım mumunu bekle.' },
    dump: { title: 'SERT DÜŞÜŞ', action: "Long açma; açık long'un varsa stop'u sıkılaştır." },
};

const Tag = ({ label, level, variant, reasons = [], testid }) => {
    if (!label) return null;
    if (level === 'LATE') {
        const pump = variant === 'pump';
        return (
            <span data-testid={testid} title={`${label}. ${reasons.join(', ')}. Ne yapmalı: kovalama yapma, geri test bekle.`}
                  className="inline-flex items-center border rounded-sm px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider cursor-help border-amber-400/50 bg-amber-400/10 text-amber-200">
                {pump ? <TrendingUp className="h-2.5 w-2.5 mr-1" /> : <TrendingDown className="h-2.5 w-2.5 mr-1" />}
                {pump ? 'PATLADI · TREN KAÇTI' : 'DÜŞTÜ · SHORT İÇİN GEÇ'}
            </span>
        );
    }
    const hi = level === 'HIGH';
    const t = TAG_TEXT[variant];
    const tip = `${label} (${hi ? 'yüksek' : 'orta'} olasılık). ${reasons.join(', ')}. Ne yapmalı: ${t.action}`;
    const styles =
        variant === 'pump'
            ? hi
                ? 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300'
                : 'border-emerald-400/20 bg-emerald-400/5 text-emerald-400/80'
            : hi
              ? 'border-rose-500/40 bg-rose-500/10 text-rose-300'
              : 'border-rose-500/20 bg-rose-500/5 text-rose-400/80';
    return (
        <span data-testid={testid} title={tip} className={`inline-flex items-center border rounded-sm px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider cursor-help ${styles}`}>
            {variant === 'pump' ? <TrendingUp className="h-2.5 w-2.5 mr-1" /> : <TrendingDown className="h-2.5 w-2.5 mr-1" />}
            {t.title} · {hi ? 'YÜKSEK' : 'ORTA'}
        </span>
    );
};

const SortHeader = ({ label, active, dir, onClick, className = '', hint }) => (
    <button
        onClick={onClick}
        title={hint}
        className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-slate-400 hover:text-slate-100 transition-colors ${className}`}
    >
        {label}
        {active ? (
            dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
        ) : (
            <ArrowUpDown className="h-3 w-3 opacity-50" />
        )}
    </button>
);

export default function ScannerTable({ rows = [], loading, onSelect, selectedSymbol, onToggleFavorite, onlyFavorites, onOnlyFavoritesChange, onlyBinance, onOnlyBinanceChange, tickPrices = {}, openPositionSymbols = new Set() }) {
    const [sort, setSort] = useState({ key: 'opportunity_score', dir: 'desc' });
    const [filter, setFilter] = useState('');

    const toggleSort = (key) => {
        setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));
    };

    const filtered = useMemo(() => {
        const q = filter.trim().toUpperCase();
        let list = q ? rows.filter((r) => r.symbol.includes(q)) : rows;
        if (onlyFavorites) list = list.filter((r) => r.is_favorite);
        if (onlyBinance) list = list.filter((r) => r.on_binance);
        const sorted = [...list].sort((a, b) => {
            // favorites pinned to top
            if ((b.is_favorite ? 1 : 0) !== (a.is_favorite ? 1 : 0)) {
                return (b.is_favorite ? 1 : 0) - (a.is_favorite ? 1 : 0);
            }
            const av = a[sort.key] ?? 0;
            const bv = b[sort.key] ?? 0;
            return sort.dir === 'asc' ? av - bv : bv - av;
        });
        return sorted;
    }, [rows, filter, sort, onlyFavorites, onlyBinance]);

    const favCount = rows.filter((r) => r.is_favorite).length;

    return (
        <div className="border border-slate-700 bg-slate-800 rounded-md overflow-hidden" data-testid="scanner-table-container">
            <div className="flex items-center justify-between p-4 border-b border-slate-700 gap-4 flex-wrap">
                <div>
                    <h2 className="text-sm font-semibold tracking-tight text-slate-100">Fırsat Tarayıcı</h2>
                    <p className="text-[11px] text-slate-400 uppercase tracking-wider mt-0.5">
                        Top {rows.length - favCount} + {favCount} favori • OKX SPOT (296+ USDT çifti)
                    </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <button
                        onClick={() => onOnlyFavoritesChange?.(!onlyFavorites)}
                        data-testid="toggle-only-favorites"
                        className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-sm border text-[11px] uppercase tracking-wider transition-colors ${
                            onlyFavorites
                                ? 'border-amber-400/40 bg-amber-400/10 text-amber-300'
                                : 'border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-slate-100'
                        }`}
                    >
                        <Star className={`h-3 w-3 ${onlyFavorites ? 'fill-amber-400 text-amber-400' : ''}`} />
                        Sadece Favoriler {favCount > 0 && `(${favCount})`}
                    </button>
                    <button
                        onClick={() => onOnlyBinanceChange?.(!onlyBinance)}
                        data-testid="toggle-only-binance"
                        className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-sm border text-[11px] uppercase tracking-wider transition-colors ${
                            onlyBinance
                                ? 'border-yellow-400/40 bg-yellow-400/10 text-yellow-300'
                                : 'border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-slate-100'
                        }`}
                        title="Sadece Binance Futures'ta işlem gören coinleri göster"
                    >
                        <span className={onlyBinance ? 'text-yellow-300' : 'text-slate-400'}>₿</span>
                        Sadece Binance
                    </button>
                    <div className="relative w-64 max-w-full">
                        <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <Input
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Tabloyu filtrele..."
                            data-testid="scanner-filter-input"
                            className="h-8 pl-8 bg-slate-900 border-slate-700 rounded-sm text-xs placeholder:text-slate-400"
                        />
                    </div>
                </div>
            </div>

            <ScannerLegend />

            <div className="overflow-x-auto hidden md:block">
                <table className="w-full text-sm min-w-[1000px]">
                    <thead>
                        <tr className="border-b border-slate-700 text-left">
                            <th className="py-2 px-2 w-[36px]" />
                            <th className="py-2 px-3 w-[140px]"><SortHeader label="Sembol" active={sort.key === 'symbol'} dir={sort.dir} onClick={() => toggleSort('symbol')} /></th>
                            <th className="py-2 px-3 text-right"><SortHeader className="justify-end w-full" label="Fiyat" active={sort.key === 'price'} dir={sort.dir} onClick={() => toggleSort('price')} /></th>
                            <th className="py-2 px-3 text-right"><SortHeader className="justify-end w-full" label="24s %" active={sort.key === 'change_pct_24h'} dir={sort.dir} onClick={() => toggleSort('change_pct_24h')} hint="Son 24 saatlik fiyat değişimi (%)" /></th>
                            <th className="py-2 px-3 text-right"><SortHeader className="justify-end w-full" label="Hacim" active={sort.key === 'volume_24h'} dir={sort.dir} onClick={() => toggleSort('volume_24h')} hint="24 saatlik işlem hacmi (USDT)" /></th>
                            <th className="py-2 px-3 text-right hidden md:table-cell"><SortHeader className="justify-end w-full" label="Fonlama %" active={sort.key === 'funding_rate_pct'} dir={sort.dir} onClick={() => toggleSort('funding_rate_pct')} hint="Perpetual funding rate. Pozitif = long kalabalık, negatif = short kalabalık. 0.05%+ aşırı sayılır." /></th>
                            <th className="py-2 px-3 text-right hidden lg:table-cell"><SortHeader className="justify-end w-full" label="L/S" active={sort.key === 'ls_ratio'} dir={sort.dir} onClick={() => toggleSort('ls_ratio')} hint="Long / Short pozisyon oranı. 1 = dengeli, 2.5+ long kalabalık (dump riski), 0.5 altı short kalabalık (squeeze potansiyeli)." /></th>
                            <th className="py-2 px-3 text-left"><SortHeader label="Öneri" active={sort.key === 'rec_score'} dir={sort.dir} onClick={() => toggleSort('rec_score')} hint="LONG/SHORT GİR ✓ RESMİ = güven ≥%80 ve R/R ≥2, işlem açılabilir. EĞİLİMİ · ZAYIF = kriterleri geçemedi, işlem açma. BEKLE = net yön yok." /></th>
                            <th className="py-2 px-3 text-left"><SortHeader label="+Kâr%" active={sort.key === 'profit_pct'} dir={sort.dir} onClick={() => toggleSort('profit_pct')} hint="Mevcut fiyattan hedef seviyeye kadar olası getiri yüzdesi (kaldıraçsız)." /></th>
                            <th className="py-2 px-3 text-left"><SortHeader label="R/R" active={sort.key === 'rr_ratio'} dir={sort.dir} onClick={() => toggleSort('rr_ratio')} hint="Risk/Reward oranı: olası kâr / olası zarar. 1.5+ kabul edilebilir, 2+ iyi, 3+ harika." /></th>
                            <th className="py-2 px-3 text-left"><SortHeader label="Fırsat" active={sort.key === 'opportunity_score'} dir={sort.dir} onClick={() => toggleSort('opportunity_score')} hint="Fırsat skoru = momentum + trend + hacim onayı + düşük risk. 70+ güçlü, 40-70 nötr-pozitif, 40 altı zayıf." /></th>
                            <th className="py-2 px-3 text-left"><SortHeader label="Risk" active={sort.key === 'risk_score'} dir={sort.dir} onClick={() => toggleSort('risk_score')} hint="Risk skoru = volatilite + funding sapması + pozisyon kalabalıklığı. 60+ yüksek risk." /></th>
                            <th className="py-2 px-3 text-left">
                                <span className="text-[10px] uppercase tracking-[0.1em] text-slate-400" title="Radar aşaması, 2 zaman dilimi uyumu ve yukarı patlama / sert düşüş olasılığı. Anlamları için üstteki 'Etiket Rehberi'ni aç.">Etiketler</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && rows.length === 0 ? (
                            <tr>
                                <td colSpan={12} className="py-10 text-center text-slate-400">
                                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Piyasa taranıyor...
                                </td>
                            </tr>
                        ) : filtered.length === 0 ? (
                            <tr>
                                <td colSpan={12} className="py-10 text-center text-slate-400 text-sm">
                                    Sonuç bulunamadı
                                </td>
                            </tr>
                        ) : (
                            filtered.map((r) => {
                                const chgTick = tickPrices[r.symbol]?.change_pct_24h ?? r.change_pct_24h;
                                const up = chgTick >= 0;
                                const active = selectedSymbol === r.symbol;
                                return (
                                    <tr
                                        key={r.symbol}
                                        onClick={() => onSelect?.(r.symbol)}
                                        data-testid={`scanner-row-${r.symbol}`}
                                        className={`border-b border-slate-700/50 cursor-pointer transition-colors ${
                                            active ? 'bg-slate-700' : 'hover:bg-slate-700/50'
                                        } ${r.is_favorite ? 'border-l-2 border-l-amber-400/60' : ''}`}
                                    >
                                        <td className="py-2 px-2 text-center">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onToggleFavorite?.(r.symbol, !r.is_favorite);
                                                }}
                                                data-testid={`star-${r.symbol}`}
                                                title={r.is_favorite ? 'Favorilerden çıkar' : 'Favorilere ekle'}
                                                className={`h-6 w-6 rounded-sm flex items-center justify-center transition-colors ${
                                                    r.is_favorite
                                                        ? 'text-amber-400 hover:text-amber-300'
                                                        : 'text-slate-400 hover:text-slate-200'
                                                }`}
                                            >
                                                <Star className={`h-3.5 w-3.5 ${r.is_favorite ? 'fill-current' : ''}`} />
                                            </button>
                                        </td>
                                        <td className="py-2 px-3">
                                            <div className="flex items-center gap-2">
                                                <span className="text-slate-50 font-medium text-sm">{r.symbol.replace('USDT', '')}</span>
                                                <span className="text-slate-400 text-[10px] uppercase">USDT</span>
                                                {r.on_binance && (
                                                    <span
                                                        data-testid={`binance-badge-${r.symbol}`}
                                                        title="Binance Futures'ta da işlem görüyor — Binance'te açabilirsin"
                                                        className="text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 rounded-sm px-1 text-[9px] font-mono font-bold leading-none py-0.5"
                                                    >
                                                        ₿
                                                    </span>
                                                )}
                                                {openPositionSymbols.has(r.symbol) && (
                                                    <span
                                                        data-testid={`open-pos-badge-${r.symbol}`}
                                                        title="Bu coinde açık pozisyonun var — ters giderse Telegram'dan bildirim alırsın"
                                                        className="inline-flex items-center gap-0.5 text-emerald-200 bg-emerald-500/15 border border-emerald-500/40 rounded-sm px-1 text-[9px] font-bold leading-none py-0.5"
                                                    >
                                                        💼 AÇIK
                                                    </span>
                                                )}
                                                {r.news_events && r.news_events.length > 0 && (
                                                    <span
                                                        data-testid={`news-badge-${r.symbol}`}
                                                        title={`Son 1 saatte haber: ${r.news_events[r.news_events.length-1].title}. Kırmızı = fiyat haberin tersine (sahte hareket), yeşil = fiyat doğruladı, mavi = izleniyor.`}
                                                        className={`inline-flex items-center gap-0.5 rounded-sm px-1 text-[9px] font-bold leading-none py-0.5 border ${
                                                            r.news_events[r.news_events.length-1].result?.startsWith('fake_')
                                                                ? 'bg-rose-500/15 text-rose-200 border-rose-500/40 animate-pulse'
                                                                : r.news_events[r.news_events.length-1].result?.startsWith('confirmed_')
                                                                  ? 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40'
                                                                  : 'bg-sky-500/15 text-sky-200 border-sky-500/40'
                                                        }`}
                                                    >
                                                        📰 HABER
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-2 px-3 num-cell text-slate-100"><LivePrice symbol={r.symbol} tick={tickPrices[r.symbol]} fallbackPrice={r.price} /></td>
                                        <td className={`py-2 px-3 num-cell ${(tickPrices[r.symbol]?.change_pct_24h ?? r.change_pct_24h) >= 0 ? 'text-emerald-400' : 'text-rose-500'}`}>
                                            {(() => {
                                                const chg = tickPrices[r.symbol]?.change_pct_24h ?? r.change_pct_24h;
                                                return `${chg >= 0 ? '+' : ''}${chg?.toFixed(2)}%`;
                                            })()}
                                        </td>
                                        <td className="py-2 px-3 num-cell text-slate-300">${fmtVol(r.volume_24h)}</td>
                                        <td className="py-2 px-3 num-cell hidden md:table-cell">
                                            <span
                                                className={`${
                                                    (r.derivatives?.funding_rate_pct ?? 0) > 0.05
                                                        ? 'text-rose-400'
                                                        : (r.derivatives?.funding_rate_pct ?? 0) < -0.05
                                                          ? 'text-emerald-400'
                                                          : 'text-slate-300'
                                                }`}
                                            >
                                                {r.derivatives?.funding_rate_pct?.toFixed(4) ?? '—'}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3 num-cell text-slate-300 hidden lg:table-cell">
                                            {r.derivatives?.long_short_ratio
                                                ? r.derivatives.long_short_ratio.toFixed(2)
                                                : '—'}
                                        </td>
                                        <td className="py-2 px-3"><div className="flex flex-col gap-1 items-start"><VerdictChip v={r.verdict} testId={`scanner-verdict-${r.symbol}`} /><RecommendationBadge rec={r.recommendation} compact testid={`rec-${r.symbol}`} /></div></td>
                                        <td className="py-2 px-3 num-cell">
                                            {r.recommendation?.action !== 'WAIT' ? (
                                                <span className={r.recommendation?.action === 'LONG' ? 'text-emerald-400' : 'text-rose-400'}>
                                                    +{r.recommendation?.profit_pct?.toFixed(2)}%
                                                </span>
                                            ) : (
                                                <span className="text-slate-500">—</span>
                                            )}
                                        </td>
                                        <td className="py-2 px-3 num-cell">
                                            {r.recommendation?.action !== 'WAIT' ? (
                                                <span className={r.recommendation?.rr_ratio >= 1.5 ? 'text-emerald-400' : 'text-amber-400'}>
                                                    {r.recommendation?.rr_ratio?.toFixed(2)}
                                                </span>
                                            ) : (
                                                <span className="text-slate-500">—</span>
                                            )}
                                        </td>
                                        <td className="py-2 px-3"><ScoreBar value={r.opportunity_score} color="emerald" /></td>
                                        <td className="py-2 px-3"><ScoreBar value={r.risk_score} color={r.risk_score > 60 ? 'rose' : r.risk_score > 30 ? 'amber' : 'zinc'} /></td>
                                        <td className="py-2 px-3">
                                            <div className="flex items-center gap-1 flex-wrap">
                                                {r.radar && (
                                                    <span
                                                        data-testid={`radar-badge-${r.symbol}`}
                                                        title={`Erken Keşif Radarı: ${stageText(r.radar)} — ${r.radar.passed_count}/7 teyit. ${r.radar.action_tr}`}
                                                        className={`inline-flex items-center rounded-sm px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider cursor-help ${stageStyle(r.radar.stage, r.radar.direction).badge}`}
                                                    >
                                                        RADAR {r.radar.direction === 'long' ? '↑ ' : r.radar.direction === 'short' ? '↓ ' : ''}{r.radar.stage === 'AC' ? 'AÇ' : r.radar.stage === 'HAZIRLAN' ? 'HAZIRLAN' : 'İZLE'} {r.radar.passed_count}/7{r.radar.leverage ? ` · ${r.radar.leverage.label}` : ''}
                                                    </span>
                                                )}
                                                {r.multi_tf && (
                                                    <span
                                                        data-testid={`multi-tf-badge-${r.symbol}`}
                                                        title={`${r.multi_tf.summary} (15 dk + 4 saat aynı yönde). İşlem için yine RESMİ etiketini bekle.`}
                                                        className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border cursor-help ${
                                                            r.multi_tf.tier === 'STRONG'
                                                                ? 'border-rose-400/60 bg-rose-500/15 text-rose-100'
                                                                : 'border-amber-400/40 bg-amber-500/10 text-amber-200'
                                                        }`}
                                                    >
                                                        {r.multi_tf.tier === 'STRONG' ? 'GÜÇLÜ' : 'ORTA'} · 2 ZAMAN {r.multi_tf.action}
                                                    </span>
                                                )}
                                                <Tag label={r.pump_tag?.label} level={r.pump_tag?.level} reasons={r.pump_tag?.reasons} variant="pump" testid={`pump-tag-${r.symbol}`} />
                                                <Tag label={r.dump_tag?.label} level={r.dump_tag?.level} reasons={r.dump_tag?.reasons} variant="dump" testid={`dump-tag-${r.symbol}`} />
                                                {!r.radar && !r.multi_tf && !r.pump_tag && !r.dump_tag && <span className="text-slate-500">—</span>}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Mobile card view */}
            <div className="md:hidden p-3 space-y-2">
                {loading && rows.length === 0 ? (
                    <div className="py-8 text-center text-slate-400 text-sm">
                        <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Piyasa taranıyor...
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="py-8 text-center text-slate-400 text-xs">
                        {onlyFavorites ? 'Henüz favori yok. ⭐ ile bir coin ekle.' : 'Sonuç bulunamadı'}
                    </div>
                ) : (
                    filtered.map((r) => (
                        <MobileCoinCard
                            key={r.symbol}
                            row={r}
                            active={selectedSymbol === r.symbol}
                            onSelect={onSelect}
                            onToggleFavorite={onToggleFavorite}
                            tick={tickPrices[r.symbol]}
                            isOpenPosition={openPositionSymbols.has(r.symbol)}
                        />
                    ))
                )}
            </div>
        </div>
    );
}
